import React from 'react';
import {defineMessages} from 'react-intl';
import {AccessControlIndicator} from '@igp/shared/access-control';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {ResourceType} from '@igp/shared/contract/pam';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import type {ViewType} from '@igp/shared/redux/view';

import {withAccessCheck} from '@access-control';
import type {KeycloakLogDataGridColumn, KeycloakLogViewModelKeys} from '@features/keycloak-log';
import {KeycloakLogDataGrid, KeycloakLogQueryFilter, keycloakLogsPageResource, useKeycloakLogs} from '@features/keycloak-log';
import {KeycloakLogDataGridColumnTypeName, KeycloakLogViewModelKeysArrayTypeName} from '@features/keycloak-log/constants';

const localized = defineMessages({
    title: {
        id: 'KeycloakLogs_title',
        defaultMessage: 'Event Logs',
    },
});

function KeycloakLogsInner() {
    const mapper = useAutoMapper();
    const viewType: ViewType = 'KeycloakLogs';
    const columns: KeycloakLogDataGridColumn[] = ['date', 'operator', 'action', 'targetUser', 'details'];
    const fields: KeycloakLogViewModelKeys[] = [
        ...new Set(
            columns.flatMap(column =>
                mapper.map<KeycloakLogDataGridColumn, KeycloakLogViewModelKeys[]>(
                    column,
                    KeycloakLogDataGridColumnTypeName,
                    KeycloakLogViewModelKeysArrayTypeName
                )
            )
        ),
    ];
    const {items, count, rawItems, filterString, searchFilter, handleFilterChange, handlePageChange, handlePageSizeChange} =
        useKeycloakLogs({
            viewType,
            fields,
            hiddenFilters: [
                {
                    key: 'default_resource_type',
                    value: [ResourceType.User, ResourceType.GroupMembership],
                },
            ],
            defaultPaging: {page: 1, pageSize: 25},
            validateFilter: () => true,
            syncWithUrl: true,
        });
    return (
        <LayoutQueue
            header={<PageHeader header={localized.title} />}
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems>
                            <KeycloakLogQueryFilter
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['operator', 'targetUser', 'opretationType', 'resourceType', 'date']}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <KeycloakLogDataGrid
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        page: searchFilter?.paging?.page,
                        pageSize: searchFilter?.paging?.pageSize,
                        rowCount: count,
                        isTotalCountUnknown: true,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{sortModel: searchFilter?.sorting}}
                    columns={columns}
                />
            }
        />
    );
}

const KeycloakLogsWithAccess = withAccessCheck(KeycloakLogsInner, keycloakLogsPageResource, AccessControlIndicator.InfoMessage);

export const KeycloakLogs = withTraceErrorBoundary(KeycloakLogsWithAccess, ErrorModal);
